import React, { useContext } from "react"
import FieldErrors from "../shared/field_errors"
import PlatformLabel from "../shared/platform_label"
import IdentifierRow from "./identifier_row"
import SigningStateSelect from "./signing_state_select"
import {
  BinaryFormContext,
  CODE_SIGNING_TYPES,
  PRIMARY_OPTIONS,
  REFINABLE_PRIMARY_TYPES,
  REFINEMENT_TYPES,
  RULES_FIELD,
  SIGNING_STATE_ALL,
} from "./models"
import type {
  BinaryRuleFields,
  BinaryRuleItem,
  PrimaryType,
  RefinementType,
} from "./models"
import type { IdentifierType } from "./models"

const FIELD_NAMES: Record<keyof BinaryRuleFields, string> = {
  displayName: "display_name",
  primaryType: "primary_type",
  bundleId: "bundle_id",
  cdhash: "cdhash",
  signingId: "signing_id",
  teamId: "team_id",
  pathPrefix: "path_prefix",
  signingStates: "signing_states",
}

function fieldName(key: number, field: keyof BinaryRuleFields): string {
  return `${RULES_FIELD}[${key}][${FIELD_NAMES[field]}]`
}

function elementId(key: number, suffix: string): string {
  return `binary-rule-${key}-${suffix}`
}

export function availableRefinementTypes(
  rule: BinaryRuleItem,
): RefinementType[] {
  return REFINEMENT_TYPES.filter(
    type => type !== rule.primaryType && !rule.refinementTypes.includes(type),
  )
}

export function isRefinable(primaryType: PrimaryType | ""): boolean {
  return REFINABLE_PRIMARY_TYPES.some(type => type === primaryType)
}

export function isCodeSigningIdentifier(
  identifierType: IdentifierType | "",
): boolean {
  return CODE_SIGNING_TYPES.some(type => type === identifierType)
}

function platformsFor(identifierType: IdentifierType): string[] {
  return isCodeSigningIdentifier(identifierType)
    ? ["macOS"]
    : ["iOS", "iPadOS", "tvOS", "visionOS"]
}

function clearRefinements(rule: BinaryRuleItem): BinaryRuleItem {
  const clearedRule: BinaryRuleItem = {
    ...rule,
    refinementTypes: [],
  }

  rule.refinementTypes.forEach(type => {
    clearedRule[type] = ""
  })

  return clearedRule
}

function withPrimaryType(
  rule: BinaryRuleItem,
  primaryType: PrimaryType | "",
): BinaryRuleItem {
  const primaryValue = rule.primaryType ? rule[rule.primaryType] : ""
  let updatedRule: BinaryRuleItem = { ...rule, primaryType }

  if (rule.primaryType) {
    updatedRule[rule.primaryType] = ""
  }

  if (primaryType) {
    updatedRule[primaryType] = rule[primaryType] || primaryValue
    updatedRule.refinementTypes = rule.refinementTypes.filter(
      type => type !== primaryType,
    )
  }

  if (primaryType && !isRefinable(primaryType)) {
    updatedRule = clearRefinements(updatedRule)
  }

  if (primaryType && !isCodeSigningIdentifier(primaryType)) {
    updatedRule = { ...updatedRule, signingStates: [] }
  }

  return updatedRule
}

function withIdentifierValue(
  rule: BinaryRuleItem,
  type: IdentifierType,
  value: string,
): BinaryRuleItem {
  const updatedRule: BinaryRuleItem = { ...rule }
  updatedRule[type] = value
  return updatedRule
}

function addRefinement(rule: BinaryRuleItem): BinaryRuleItem {
  const [type] = availableRefinementTypes(rule)
  if (!type) {
    return rule
  }

  return { ...rule, refinementTypes: [...rule.refinementTypes, type] }
}

function withRefinementType(
  rule: BinaryRuleItem,
  from: RefinementType,
  to: RefinementType,
): BinaryRuleItem {
  if (from === to) {
    return rule
  }

  const updatedRule: BinaryRuleItem = {
    ...rule,
    refinementTypes: rule.refinementTypes.map(type =>
      type === from ? to : type,
    ),
  }
  updatedRule[to] = rule[from]
  updatedRule[from] = ""
  return updatedRule
}

function toggleSigningState(
  rule: BinaryRuleItem,
  state: string,
  isSelected: boolean,
): BinaryRuleItem {
  if (state === SIGNING_STATE_ALL) {
    return {
      ...rule,
      signingStates: isSelected ? [SIGNING_STATE_ALL] : [],
    }
  }

  const signingStates = isSelected
    ? [...rule.signingStates, state]
    : rule.signingStates.filter(selected => selected !== state)

  return { ...rule, signingStates }
}

function removeRefinement(
  rule: BinaryRuleItem,
  type: RefinementType,
): BinaryRuleItem {
  const updatedRule: BinaryRuleItem = {
    ...rule,
    refinementTypes: rule.refinementTypes.filter(openType => openType !== type),
  }
  updatedRule[type] = ""
  return updatedRule
}

interface BinaryRuleProps {
  rule: BinaryRuleItem
  index: number
  onChange: (rule: BinaryRuleItem) => void
  onRemove: () => void
}

export default function BinaryRule({
  rule,
  index,
  onChange,
  onRemove,
}: BinaryRuleProps) {
  const { mode, disabled, tooltips } = useContext(BinaryFormContext)

  const primaryValue = rule.primaryType ? rule[rule.primaryType] : ""
  const canAddRefinement =
    isRefinable(rule.primaryType) && availableRefinementTypes(rule).length > 0
  const showSigningStates =
    isCodeSigningIdentifier(rule.primaryType) || rule.signingStates.length > 0

  const title = rule.displayName ? (
    <strong>{rule.displayName}</strong>
  ) : (
    <strong>Binary rule #{index + 1}</strong>
  )

  const headingId = elementId(rule.key, "heading")
  const displayNameId = elementId(rule.key, "display-name")
  const descriptionId = elementId(rule.key, "description")
  const baseErrorId = rule.errors.base?.length
    ? elementId(rule.key, "errors")
    : undefined

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      aria-describedby={baseErrorId}
      className="binary-rule-card panel panel-default"
    >
      <div id={headingId} className="panel-heading">
        {title}
        {rule.primaryType
          ? platformsFor(rule.primaryType).map(platform => (
              <PlatformLabel key={platform}>{platform}</PlatformLabel>
            ))
          : null}
      </div>
      <div className="panel-body">
        <FieldErrors
          errors={rule.errors.base}
          className="binary-rule-errors"
          id={baseErrorId}
        />

        <div className="binary-rule-label-field">
          <label className="binary-rule-inline-label" htmlFor={displayNameId}>
            Label
          </label>
          <input
            type="text"
            id={displayNameId}
            className="form-control binary-rule-value"
            name={fieldName(rule.key, "displayName")}
            placeholder="Optional — a name for this rule (for your reference, not sent to the device)"
            value={rule.displayName}
            disabled={disabled}
            onChange={event =>
              onChange({ ...rule, displayName: event.target.value })
            }
          />
        </div>

        <IdentifierRow
          prefix={
            <span className="binary-rule-inline-label">Identify by</span>
          }
          rowId={elementId(rule.key, "primary")}
          describedBy={rule.primaryType ? descriptionId : undefined}
          name={
            rule.primaryType ? fieldName(rule.key, rule.primaryType) : undefined
          }
          typeName={fieldName(rule.key, "primaryType")}
          type={rule.primaryType}
          value={primaryValue}
          options={PRIMARY_OPTIONS[mode]}
          blankOption="Select an identifier…"
          removeIcon="glyphicon-remove-circle"
          removeLabel="Remove"
          errors={rule.primaryType ? rule.errors[rule.primaryType] : undefined}
          disabled={disabled}
          onChangeType={type => onChange(withPrimaryType(rule, type))}
          onChangeValue={value =>
            rule.primaryType &&
            onChange(withIdentifierValue(rule, rule.primaryType, value))
          }
          onRemove={onRemove}
        />

        {rule.primaryType && (
          <p id={descriptionId} className="help-block binary-rule-description">
            {tooltips[rule.primaryType]}
          </p>
        )}

        {rule.refinementTypes.map(type => (
          <IdentifierRow
            key={type}
            prefix={<span className="binary-rule-and">and</span>}
            rowId={elementId(rule.key, type)}
            name={fieldName(rule.key, type)}
            type={type}
            value={rule[type]}
            options={[type, ...availableRefinementTypes(rule)]}
            removeIcon="glyphicon-remove"
            errors={rule.errors[type]}
            disabled={disabled}
            onChangeType={selectedType =>
              selectedType &&
              onChange(withRefinementType(rule, type, selectedType))
            }
            onChangeValue={value =>
              onChange(withIdentifierValue(rule, type, value))
            }
            onRemove={() => onChange(removeRefinement(rule, type))}
          />
        ))}

        {showSigningStates && (
          <SigningStateSelect
            name={fieldName(rule.key, "signingStates")}
            selected={rule.signingStates}
            errors={rule.errors.signingStates}
            onChange={(state, isSelected) =>
              onChange(toggleSigningState(rule, state, isSelected))
            }
          />
        )}

        {!disabled && canAddRefinement && (
          <button
            type="button"
            className="btn btn-link binary-rule-add-condition"
            onClick={() => onChange(addRefinement(rule))}
          >
            <span className="glyphicon glyphicon-plus" /> Add condition
          </button>
        )}

        {!primaryValue && (
          <p className="help-block binary-rule-needs-identifier">
            Choose an identifier and enter a value.
          </p>
        )}
      </div>
    </div>
  )
}
